import { useEffect } from 'react'

import { InputGroup } from '@/components/inputs/InputGroup'
import type { FormSectionProps } from '@/components/inputs/types'
import { Loading } from '@/components/Loading'

import { useBookingFormCtx } from '../book/BookingFormProvider'
import type { CustomerRoutedDetail } from '../book/types'
import { useAccountSettings } from '../settings/account-settings/hooks'
import { ShipmentFormSection } from '../shipment-form/ShipmentFormSection'
import { BillToForm } from './BillToForm'
import { billToMapper } from './billToFuncs'
import { useFetchLoadBillTo } from './useFetchLoadBillTo'

export const BillTo = (props: FormSectionProps) => {
  const actions = useBookingFormCtx(s => s.actions)
  const billTo = useBookingFormCtx(s => s.billTo)
  const loadId = useBookingFormCtx(s => s.loadId)
  const workflow = useBookingFormCtx(s => s.workflow)
  const isFreightCollect = useBookingFormCtx(s => s.isFreightCollect)
  const isNew = useBookingFormCtx(s => s.isNew)
  const settings = useAccountSettings()
  const terms = useBookingFormCtx(s => s.customerRoutedDetail.terms)
  const show =
    isFreightCollect ||
    (settings.booking?.showBillToOnBooking && workflow !== 'parcel')

  const showForm =
    workflow !== 'parcel' &&
    ((isFreightCollect && terms === 'third-party') ||
      (!isFreightCollect && settings.booking?.showBillToOnBooking))

  const computedBillTo = useFetchLoadBillTo(
    show && !isNew ? loadId : '',
    !!billTo.address,
  )
  const label =
    workflow === 'parcel' ? 'Bill to' : 'Bill to address (for bill of lading)'
  const termOpts: Array<{
    label: string
    value: CustomerRoutedDetail['terms']
  }> = [
    {
      label: 'Same as consignee (outbound collect)',
      value: 'outbound-collect',
    },
    {
      label:
        workflow === 'parcel'
          ? 'Third party'
          : 'Enter a different address (third party)',
      value: 'third-party',
    },
  ]

  useEffect(() => {
    if (computedBillTo.data?.address) {
      actions.setBillToValues(billToMapper.dtoToForm(computedBillTo.data))
    }
  }, [computedBillTo.data, actions])

  if (!show) return null

  if (computedBillTo.isFetching) {
    return (
      <ShipmentFormSection
        title="Bill to"
        className="gray-container"
        pillClassName="default-container !border-2"
      >
        <Loading />
      </ShipmentFormSection>
    )
  }

  return (
    <ShipmentFormSection
      title="Bill to"
      className="gray-container"
      pillClassName="default-container !border-2"
    >
      <div className="form-row">
        {isFreightCollect && (
          <InputGroup
            className="basis-full"
            inputProps={{
              name: 'billToTerms',
              onChange: e => {
                actions.setCustomerRoutedValues({
                  terms: e.target.value as CustomerRoutedDetail['terms'],
                })
              },
              options: termOpts,
              value: terms,
            }}
            inputType="radio"
            label={label}
          />
        )}

        {showForm && (
          <BillToForm
            {...props}
            workflow={workflow}
            autoFocus={terms === 'third-party'}
            required={true}
            requirePhone
            setValues={actions.setBillToValues}
            values={billTo}
          />
        )}
      </div>
    </ShipmentFormSection>
  )
}
